import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Icon from '../Icon/Icon';
import {
  formatIsoDateToMasked,
  maskDate,
  parseMaskedDateToIso,
} from '../../../utils/masks';
import { AppTypography } from '../appUi';
import AppFieldLine from './AppFieldLine';
import AppInputLabel from './AppInputLabel';
import styles from './AppFormBirthDateInput.module.css';

interface AppFormBirthDateInputProps {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  containerClassName?: string;
  plainCalendarButton?: boolean;
  onChange: (isoDate: string) => void;
}

const AppFormBirthDateInput: React.FC<AppFormBirthDateInputProps> = ({
  id,
  name,
  label,
  value,
  error,
  required = false,
  disabled = false,
  placeholder = 'DD/MM/AAAA',
  containerClassName = '',
  plainCalendarButton = false,
  onChange,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const datePickerRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState(() => formatIsoDateToMasked(value));

  useEffect(() => {
    setDisplayValue(formatIsoDateToMasked(value));
  }, [value]);

  const commitMaskedValue = useCallback((maskedValue: string) => {
    const digits = maskedValue.replace(/\D/g, '');

    if (!digits) {
      onChange('');
      return;
    }

    if (digits.length === 8) {
      const isoDate = parseMaskedDateToIso(maskedValue);
      if (isoDate) {
        onChange(isoDate);
        setDisplayValue(formatIsoDateToMasked(isoDate));
      }
    }
  }, [onChange]);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = maskDate(event.target.value);
    setDisplayValue(maskedValue);
    commitMaskedValue(maskedValue);
  };

  const handleTextBlur = () => {
    if (!displayValue.trim()) {
      onChange('');
      return;
    }

    const isoDate = parseMaskedDateToIso(displayValue);
    if (isoDate) {
      onChange(isoDate);
      setDisplayValue(formatIsoDateToMasked(isoDate));
      return;
    }

    if (value) {
      setDisplayValue(formatIsoDateToMasked(value));
      return;
    }

    setDisplayValue(displayValue);
  };

  const handleCalendarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const openCalendar = () => {
    if (disabled || !datePickerRef.current) {
      return;
    }

    if (typeof datePickerRef.current.showPicker === 'function') {
      datePickerRef.current.showPicker();
      return;
    }

    datePickerRef.current.click();
  };

  const inputClassName = styles.input;

  return (
    <div className={`${styles.container} ${containerClassName}`}>
      {label && (
        <AppInputLabel htmlFor={inputId} required={required}>
          {label}
        </AppInputLabel>
      )}

      <div className={styles.inputRow}>
        <input
          id={inputId}
          name={name}
          type="text"
          className={inputClassName}
          value={displayValue}
          onChange={handleTextChange}
          onBlur={handleTextBlur}
          placeholder={placeholder}
          inputMode="numeric"
          autoComplete="bday"
          maxLength={10}
          disabled={disabled}
          aria-invalid={Boolean(error)}
        />

        <button
          type="button"
          className={`${styles.calendarButton} ${plainCalendarButton ? styles.calendarButtonPlain : ''}`.trim()}
          onClick={openCalendar}
          disabled={disabled}
          aria-label="Abrir calendário"
        >
          <Icon name="calendar_month" size={18} className={styles.calendarIcon} />
        </button>

        <input
          ref={datePickerRef}
          type="date"
          className={styles.hiddenDatePicker}
          value={value}
          onChange={handleCalendarChange}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden
        />
      </div>

      <AppFieldLine error={Boolean(error)} />
      {error ? (
        <AppTypography variant="caption" className={styles.errorText}>
          {error}
        </AppTypography>
      ) : null}
    </div>
  );
};

export default AppFormBirthDateInput;
